import { logSessionInfo } from '../services/logger';
import { getUserData } from './API';

export const NetworkStatus = Object.freeze({
  Normal: 'Normal',
  Bad: 'Bad',
});

const audioQueueLengthCacheCutoff = 8; // How long should the audio queue length cache be before we allow analysis
const MAX_QUEUE_LENGTH_SAMPLES = 64; // Rolling cap for the decision buffer; only the last cutoff samples drive the decision, so older ones are safe to drop
const MAX_ANALYTICS_BEFORE_FLUSH = 500; // Flush the diagnostic dump if a prolonged bad-network limbo never reaches a transition/phrase-end (the usual flush points)

// Nearest-rank percentile on an already-sorted ascending array. null if empty —
// a dump with no matched send→ack pairs must not invent 0 ms.
function percentileNearestRank(sortedAsc, q) {
  if (!sortedAsc.length) {
    return null;
  }
  const rank = Math.max(0, Math.ceil(q * sortedAsc.length) - 1);
  return sortedAsc[rank];
}

function dumpFromAckMs(ackMs) {
  const sorted = ackMs.filter((ms) => Number.isFinite(ms)).sort((a, b) => a - b);
  return {
    ackedCount: sorted.length,
    ackP50Ms: percentileNearestRank(sorted, 0.5),
    ackP95Ms: percentileNearestRank(sorted, 0.95),
    ackMaxMs: sorted.length ? sorted[sorted.length - 1] : null,
    ackMs: sorted,
  };
}

export default class NetworkMonitoringService {
  status = NetworkStatus.Normal;

  audioQueue = []; // Holds representations of audio segments that have been sent to server, but have not yet been ack'd
  audioQueueLengthCache = []; // Stores audio queue lengths to monitor how it is changing over time
  audioQueueLengthCacheTimes = []; // Timestamps parallel to audioQueueLengthCache (same push/reset lifecycle) so the decision window's wall-clock span is exact
  audioMonitoringActive = false; // If the service should process updates and analysis

  badNetworkPending = false; // If we are in process of checking if we should enter bad network state
  badNetworkPendingTracker = []; // Stores audio queue lengths while we check if the network is bad
  normalNetworkPending = false; // If we are in process of checking if we should return to normal network state
  normalNetworkPendingTracker = []; // Stores audio queue lengths while we check if the network is back to normal

  audioQueueAnalyticsSnapshots = []; // Stores snapshots of the audio queue length (occasionally, this will be sent in an analytics event and then reset)
  dequeueAnalytics = []; // Stores dequeue IDs for analysis
  onDecodedAnalytics = []; // Stores onDecoded events for analysis
  // Matched send→ack samples for the open phrase. Window flushes (Bad↔Normal,
  // size cap) fold into this so handlePhraseEnd is not only the last window.
  phraseAckMs = [];

  badNetworkCount = 0;
  // Persist-time audioMonitoringActive is usually false (phrase-end tears it
  // down). This stays true if monitoring armed at least once this activity.
  monitoringArmedThisActivity = false;

  analyticsQueue = []; // If analytics failed to send (due to poor network), we store them here to try again when returning to stable network

  networkStatusCallback = () => {}; // Callback to notify listeners of network status change

  _cooldownUntil = 0; // Timestamp until which analysis is suppressed (independent of audioMonitoringActive)

  constructor(networkStatusCallback) {
    this.setAudioMonitoringActive(false);
    this.networkStatusCallback = networkStatusCallback;
    console.log("[NetworkMonitoringService] Initialized");
  }

  /**
 * Current network status setter
 * @returns {boolean}
 */
  setNetworkStatus = (newStatus, isAdministrative = false) => {
    // isAdministrative: the transition comes from monitoring being turned off,
    // not from evidence that the network recovered. Listeners must clean up
    // timers but not run recovery actions (which restart listening).
    const oldStatus = this.status;
    this.status = newStatus;
    switch (this.status) {
      case NetworkStatus.Bad:
        this.badNetworkCount++;
        break;
    }

    // Diagnostic snapshot of the decision window that drove this transition.
    // windowStart comes from audioQueueLengthCacheTimes (same push/reset lifecycle
    // as the cache) so it always aligns with the sliced window — the snapshots
    // array resets on every save and would misindex, silently reporting 0.
    const window = this.audioQueueLengthCache.slice(audioQueueLengthCacheCutoff * -1);
    const windowStart = this.audioQueueLengthCacheTimes[this.audioQueueLengthCacheTimes.length - window.length];
    const windowSpanMs = window.length && windowStart ? Date.now() - windowStart : 0;
    this.logAnalytics('network_status_changed', {
      event: newStatus,
      isAdministrative,
      decisionWindow: window, // last N sampled queue lengths
      windowSpanMs, // wall-clock time the decision window covered
      cacheLength: this.audioQueueLengthCache.length,
      currentQueueLength: this.audioQueue.length,
      badNetworkCount: this.badNetworkCount,
    })

    // Loud, unmissable console marker at the moment the status flips.
    if (newStatus === NetworkStatus.Bad) {
      console.warn(
        `%c[NMS] ⚠️ ENTERED BAD NETWORK — window=[${window.join(',')}] spanMs=${windowSpanMs} count=${this.badNetworkCount}`,
        'background:#c00;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px;'
      );
    } else {
      console.warn(
        `%c[NMS] ✓ back to NORMAL — window=[${window.join(',')}] spanMs=${windowSpanMs}`,
        'background:#080;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px;'
      );
    }

    // Flush the queue trajectory at the moment of transition so the ramp is captured
    // even if the phrase does not end.
    this.saveAudioQueueAnalyticsSnapshots();

    if (newStatus === NetworkStatus.Normal) {
      for (const analyticsEvent of this.analyticsQueue) {
        this.logAnalytics(
          analyticsEvent.eventName,
          analyticsEvent.data,
          analyticsEvent.timestamp
        );
      }

      this.analyticsQueue = [];
    }

    if (getUserData().showBadNetworkResponse && this.networkStatusCallback) {
      this.networkStatusCallback(oldStatus, newStatus, isAdministrative);
    }
  }

  /**
 * Does any necessary clean up work when this NMS instance is no longer needed.
 * @returns {boolean}
 */
  cleanUp() {
    console.log("[NetworkMonitoringService] Cleaning up")
  }

  /**
 * Saves analytics using logSessionInfo, retries on fail, and then adds to analyticsQueue for more retries on continued fail
 * @param {string} eventName - Name of analytics event
 * @param {Object} data - Extra data to add to event
 * @param {number} timestampOverride - Unix timestamp for event
 * @param {boolean} isRetry - If this attempt is a retry. If it is not a retry, then on fail we retry immediately. If it is a retry, then on fail we add to analyticsQueue for future attempts on stable network.
 * @returns {null}
 */
  logAnalytics(eventName, data = {}, timestamp = Date.now(), isRetry = false) {
    logSessionInfo(eventName, { ...data, timestamp }).then((success) => {
      if (!success) {
        if (!isRetry) {
          const retrying = true;
          this.logAnalytics(eventName, data, timestamp, retrying);
        } else {
          this.analyticsQueue.push({
            eventName,
            data,
            timestamp
          })
        }
      }
    });
  }

  /**
 * Checks if the parameters NMS is watching currently indicate slow network.
 * @returns {boolean}
 */
  badNetworkCriteriaMet() {
    if (this.audioQueueLengthCache.length < audioQueueLengthCacheCutoff) {
      // We should not be checking badNetworkCriteriaMet if audioQueueLengthCache has less than 4 elements
      return false;
    }

    // Get the last four elements in the audioQueueLengthCache
    const elementsToAnalyze = this.audioQueueLengthCache.slice(audioQueueLengthCacheCutoff * -1);

    // Check if any element is lower than the previous element, indicating that the queue is decreasing over time (i.e. NOT bad network)
    const anyElementIsDescending = elementsToAnalyze.some((num, index) => {
      return index > 0 && num < elementsToAnalyze[index - 1];
    });

    // Check if the final element is at least 2 greater than the first element, indicating that the audio queue is growing rapidly enough to cause issues (i.e. bad network)
    const elementsIncreasingQuickly = elementsToAnalyze[audioQueueLengthCacheCutoff - 1] - elementsToAnalyze[0] >= (audioQueueLengthCacheCutoff - 2);

    return !anyElementIsDescending && elementsIncreasingQuickly;
  }

  /**
   * Starts a cooldown period during which queue sampling and analyzeAudioQueue() are suppressed.
   * Flushes stale tracking data but does not change audioMonitoringActive state.
   * Use when an external event (e.g. audio device change) would produce misleading queue data.
   * @param {number} durationMs - How long the cooldown lasts in milliseconds
   */
  startCooldown(durationMs) {
    this._cooldownUntil = Date.now() + durationMs;
    this.audioQueue = [];
    this.audioQueueLengthCache = [];
    this.audioQueueLengthCacheTimes = [];
    this.badNetworkPending = false;
    this.badNetworkPendingTracker = [];
    this.normalNetworkPending = false;
    this.normalNetworkPendingTracker = [];
    console.log(`[NetworkMonitoringService] Cooldown started for ${durationMs}ms`);
    this.logAnalytics('network_monitoring_cooldown_started', { durationMs });
  }

  /**
   * @returns {boolean} Whether analysis is currently suppressed by a cooldown
   */
  isInCooldown() {
    return Date.now() < this._cooldownUntil;
  }

  // ***********************
  // Audio queue monitoring
  // ***********************

  /**
 * Enqueues a new audio segment representation and stores the time that it was enqueued.
 * @param {number} audioId - Identifier for an audio segment in the audioQueue. This will be the sequenceNumber from SpeechStream.js
 * @returns {undefined}
 */
  enqueueAudio(audioId) {
    if (!this.audioMonitoringActive || this.isInCooldown()) {
      return;
    }

    const audio = {
        id: audioId,
        timestamp: Date.now()
    };
    this.audioQueue.push(audio);
    this.recordQueueLengthSample();

    // Slim snapshot: id + queue length only (extraData omitted to stay under the
    // debug logger's per-message truncation limit).
    this.audioQueueAnalyticsSnapshots.push({
      q: this.audioQueue.length,
      id: audioId,
      t: Date.now(),
    });
    this.maybeFlushAnalyticsSnapshots();

    this.analyzeAudioQueue();
  }

  /**
   * Pushes the current queue length + timestamp onto the decision buffer, trimming
   * to a rolling window. badNetworkCriteriaMet only reads the last cutoff samples,
   * so trimming bounds memory during a prolonged bad-network pause — where
   * monitoring stays active and no transition/phrase-end ever resets these.
   * @returns {undefined}
   */
  recordQueueLengthSample() {
    this.audioQueueLengthCache.push(this.audioQueue.length);
    this.audioQueueLengthCacheTimes.push(Date.now());
    if (this.audioQueueLengthCache.length > MAX_QUEUE_LENGTH_SAMPLES) {
      this.audioQueueLengthCache.shift();
      this.audioQueueLengthCacheTimes.shift();
    }
  }

  /**
   * Bounds the diagnostic arrays during a prolonged bad-network session that never
   * reaches a transition or phrase-end (the usual flush points). Flushing preserves
   * the diagnostics as a dump instead of dropping them.
   * @returns {undefined}
   */
  maybeFlushAnalyticsSnapshots() {
    if (this.audioQueueAnalyticsSnapshots.length >= MAX_ANALYTICS_BEFORE_FLUSH ||
        this.dequeueAnalytics.length >= MAX_ANALYTICS_BEFORE_FLUSH) {
      this.saveAudioQueueAnalyticsSnapshots();
    }
  }

  /**
 * Dequeues an audio segment representation based on the provided id.
 * @param {number} audioId - Identifier for an audio segment in the audioQueue. This will be the sequenceNumber from SpeechStream.js
 * @returns {undefined}
 */
  dequeueAudio(audioId) {
    const index = this.audioQueue.findIndex(audio => audio.id === audioId);
    let ackMs = null;

    if (index !== -1) {
      // Pair against the live queue entry, not analytics snapshots. Snapshots
      // reset on every dump; the queue (and its send timestamp) survives.
      ackMs = Date.now() - this.audioQueue[index].timestamp;
      // Date.now() can step backward (NTP). A negative would pull p50 down.
      if (ackMs < 0) {
        ackMs = null;
      }
      this.audioQueue.splice(index, 1);
    }

    // Record the ack before analyzeAudioQueue: a Bad/Normal transition flushes
    // the dump, and this dequeue is the one we need in that dump.
    if (this.audioMonitoringActive) {
      this.dequeueAnalytics.push({ id: audioId, matched: index !== -1, ackMs });
    }

    if (index !== -1 && this.audioMonitoringActive && !this.isInCooldown()) {
      // Sample the drained queue so acks are visible to the detector immediately
      // instead of one enqueue later. A drain writes a descending sample — the
      // "not bad network" signal — and lets Bad->Normal recovery proceed even
      // when no new segments are being sent.
      this.recordQueueLengthSample();
      this.analyzeAudioQueue();
    }

    if (this.audioMonitoringActive) {
      this.maybeFlushAnalyticsSnapshots();
    }
  }

  /**
 * Determines if we should enter bad network state due to backed up audio queue or return to normal state after audio queue clears.
 * @returns {undefined}
 */
  analyzeAudioQueue() {
    if (this.isInCooldown()) {
      return;
    }

    if (this.audioQueueLengthCache.length < audioQueueLengthCacheCutoff) {
      // Don't do any analysis until we have at least audioQueueLengthCacheCutoff data points
      return;
    }

    if (this.status === NetworkStatus.Normal && !this.badNetworkPending && this.badNetworkCriteriaMet()) {
      // We'll give the audio queue a few cycles to go back down. If we get enough bad network checks in a row, enter slow network state
      this.badNetworkPending = true;
      this.badNetworkPendingTracker = [];
    } else if (this.badNetworkPending) {
      if (!this.badNetworkCriteriaMet()) {
        // If we ever don't meet the bad network criteria, reset the bad network pending state
        this.badNetworkPendingTracker = [];
        this.badNetworkPending = false;
        return;
      }

      // We must meet the bad network criteria 14 times (about 3 seconds) before we enter bad network state to avoid false positives
      this.badNetworkPendingTracker.push(true);
      if (this.badNetworkPendingTracker.length >= 14) {
        // We've hit enough bad network checks in a row to enter bad network state
        this.setNetworkStatus(NetworkStatus.Bad);
        this.badNetworkPendingTracker = [];
        this.badNetworkPending = false;
      }
    } else if (this.status === NetworkStatus.Bad && !this.normalNetworkPending && !this.badNetworkCriteriaMet()) {
      // Start process of going back to normal network if we continue to meet the criteria for normal network
      this.normalNetworkPending = true;
      this.normalNetworkPendingTracker = [];
    } else if (this.normalNetworkPending) {
      // Ensure the network does not meet bad network criteria for a 6 cycles before moving back to normal state
      if (this.badNetworkCriteriaMet()) {
        // If we ever meet the bad network criteria, reset the normal network pending state
        this.normalNetworkPendingTracker = [];
        this.normalNetworkPending = false;
        return;
      }
      this.normalNetworkPendingTracker.push(true);

      if (this.normalNetworkPendingTracker.length >= 6) {
        // We've passed enough cycles to move back to normal state
        this.setNetworkStatus(NetworkStatus.Normal);
        this.normalNetworkPendingTracker = [];
        this.normalNetworkPending = false;
      }
    }
  }

  /**
   * Logs the current window, folds its matched ack samples into the open phrase, then clears the window.
   * @returns {{ackedCount: number, ackP50Ms: number|null, ackP95Ms: number|null, ackMaxMs: number|null, ackMs: number[]}}
   */
  saveAudioQueueAnalyticsSnapshots() {
    const enq = this.audioQueueAnalyticsSnapshots;
    const deq = this.dequeueAnalytics;
    const enqIds = enq.map(e => e.id);
    const matchedDeqIds = new Set(deq.filter(d => d.matched).map(d => d.id));
    // Phantoms: enqueued ids that were never removed by a matching ack.
    const neverDequeued = [...new Set(enqIds.filter(id => !matchedDeqIds.has(id)))];
    // Duplicate ids enqueued (id-collision bug).
    const seen = new Set(), dupes = new Set();
    for (const id of enqIds) { if (seen.has(id)) dupes.add(id); else seen.add(id); }

    const windowDump = dumpFromAckMs(deq.map((d) => d.ackMs));
    this.phraseAckMs.push(...windowDump.ackMs);
    this.logAnalytics('network_monitoring_service_queue_data_dump', {
      enqCount: enq.length,
      deqCount: deq.length,
      deqUnmatched: deq.filter(d => !d.matched).length,
      maxQueueLen: enq.reduce((m, e) => Math.max(m, e.q), 0),
      endQueueLen: this.audioQueue.length,
      neverDequeuedCount: neverDequeued.length,
      neverDequeued: neverDequeued.slice(0, 40),
      duplicateEnqIds: [...dupes].slice(0, 40),
      ackedCount: windowDump.ackedCount,
      ackP50Ms: windowDump.ackP50Ms,
      ackP95Ms: windowDump.ackP95Ms,
      ackMaxMs: windowDump.ackMaxMs,
    });
    this.audioQueueAnalyticsSnapshots = [];
    this.dequeueAnalytics = [];
    this.onDecodedAnalytics = [];
    return windowDump;
  }

  resetForNewActivity() {
    this.badNetworkCount = 0;
    this.phraseAckMs = [];
    this.monitoringArmedThisActivity = false;
  }

  drainPhraseAcks() {
    this.saveAudioQueueAnalyticsSnapshots();
    this.phraseAckMs = [];
  }

  /**
 * audioMonitoringActive setter
 * @param {boolean} isActive - If the service should process updates and analysis
 * @returns {undefined}
 */
  setAudioMonitoringActive(isActive) {
    this.audioMonitoringActive = isActive;
    if (isActive) {
      this.monitoringArmedThisActivity = true;
    }
    if (!isActive) {
      // No monitoring => no claim about the network. Clear Bad before wiping
      // queue data so the transition log still captures the decision window.
      // Administrative: monitoring is being turned off (intervention, listening
      // stop, phrase end) — this is not evidence the network recovered, so
      // listeners must not run recovery actions on this transition.
      if (this.status === NetworkStatus.Bad) {
        this.setNetworkStatus(NetworkStatus.Normal, true);
      }

      // Reset audio monitoring data
      this.audioQueue = [];
      this.audioQueueLengthCache = [];
      this.audioQueueLengthCacheTimes = [];
      this.badNetworkPending = false;
      this.badNetworkPendingTracker = [];
      this.normalNetworkPending = false;
      this.normalNetworkPendingTracker = [];
    }
  }

  /**
   * Phrase-end cleanup. Snapshot send→ack stats before deactivating so a lingering
   * Bad→Normal administrative transition cannot wipe the dump we persist.
   * @returns {{ackedCount: number, ackP50Ms: number|null, ackP95Ms: number|null, ackMaxMs: number|null, ackMs: number[]}}
   */
  handlePhraseEnd() {
    this.saveAudioQueueAnalyticsSnapshots();
    const dump = dumpFromAckMs(this.phraseAckMs);
    this.phraseAckMs = [];
    // setAudioMonitoringActive(false) also resets a lingering Bad status to Normal
    this.setAudioMonitoringActive(false);
    return dump;
  }

  /**
 * Called by SpeechStream when audio is decoded. Only used for analytics.
 * @param {number} audioId - Identifier for an audio segment in the audioQueue. This will be the sequenceNumber from SpeechStream.js
 * @returns {undefined}
 */
  handleOnDecoded() {
    this.onDecodedAnalytics.push({
      timestamp: Date.now(),
    });
  }
}